import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppstoreOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FolderOutlined,
  HistoryOutlined,
  PieChartOutlined,
  ReloadOutlined,
  RobotOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { LayoutDashboard } from 'lucide-react';

import { Bento, DataTable, PageHeader, StatTile, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, ngayVN, soVN } from '../../../lib/dinh-dang';
import { luuTep } from '../../../lib/luu-tep';
import { useToast } from '../../../lib/toast';
import { KhongCoQuyen, LoiRpa } from '../components/chung';
import { HopBaoCaoInstance } from '../components/lich-su/HopBaoCaoInstance';
import { BadgeTrangThaiLuot } from '../components/lich-su/nhan';
import { CotTheoNgay, DanhSachRobot, ThanhTheoProject, VanhTyLe } from '../components/tong-quan/BieuDoTongQuan';
import { QUYEN, TRANG_THAI_INSTANCE } from '../constants';
import { KHOA_RPA } from '../hooks/useRpa';
import { useInstancesTongQuan, useSoLieuTongQuan, type TruyVanTongQuan } from '../hooks/useTongQuan';
import { ngayChoTenTep, taoCsv } from '../nghiep-vu/csv';
import {
  dangChay,
  dongCsvTongQuan,
  KHOANG_NGAY_TONG_QUAN,
  locInstance,
  phanTram,
  SO_NGAY_MAC_DINH,
  tachThoiLuong,
  thoiLuongMs,
  TRANG_LUOT_TONG_QUAN,
  TRANG_THAI_LOC_TONG_QUAN,
  type BoLocTongQuan,
} from '../nghiep-vu/tong-quan';
import type { InstanceTongQuan, TongQuanRpa } from '../types';

const TONE = 'orange';

/**
 * Dashboard RPA (`/rpa`, `RpaDashboardView` của native): lượt chạy Robot, tỷ lệ thành công và tình trạng Robot theo
 * Project — đọc instance OpenRPA trên OpenCore của tenant trong 7/14/30 ngày gần nhất. Số liệu và bảng lượt chạy tự làm
 * mới mỗi 30 giây; bảng lọc, tìm ở máy khách như native.
 */
export function TongQuanView() {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const toast = useToast();
  const quyen = useModulePermissions(QUYEN);
  const [truyVan, setTruyVan] = useState<TruyVanTongQuan>({ days: SO_NGAY_MAC_DINH, projectId: '' });
  const soLieu = useSoLieuTongQuan(truyVan, quyen.tongQuanDoc);
  const luot = useInstancesTongQuan(truyVan, quyen.tongQuanDoc);
  const tieuDe = t('nav.muc.dashboardRpa');
  const dsLuot = luot.data?.data ?? [];

  const xuatCsv = () => {
    const csv = taoCsv(
      [
        t('rpaTongQuan.csv.instanceId'),
        t('rpaTongQuan.csv.batDau'),
        t('rpaTongQuan.csv.ketThuc'),
        t('rpaTongQuan.csv.project'),
        t('rpaTongQuan.csv.workflow'),
        t('rpaTongQuan.csv.may'),
        t('rpaTongQuan.csv.trangThai'),
        t('rpaTongQuan.csv.thoiLuongMs'),
        t('rpaTongQuan.csv.loi'),
      ],
      dongCsvTongQuan(dsLuot, (iso) => ngayGioVN(iso, locale)),
    );
    luuTep(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `dashboard-rpa-${ngayChoTenTep(new Date())}.csv`);
    toast.success(t('rpaTongQuan.daXuat'));
  };

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: tieuDe }]}
        titleIcon={<LayoutDashboard size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaTongQuan.moTa')}
        actions={
          quyen.tongQuanDoc ? (
            <>
              <Button
                variant="outline"
                size="md"
                icon={<ReloadOutlined />}
                loading={(soLieu.isFetching && !soLieu.isPending) || (luot.isFetching && !luot.isPending)}
                onClick={() => void qc.invalidateQueries({ queryKey: KHOA_RPA.tongQuan })}
              >
                {t('chung.lamMoi')}
              </Button>
              <Button variant="outline" size="md" icon={<DownloadOutlined />} disabled={dsLuot.length === 0} onClick={xuatCsv}>
                {t('rpaTongQuan.xuatCsv')}
              </Button>
            </>
          ) : undefined
        }
      />

      {!quyen.tongQuanDoc ? (
        <KhongCoQuyen quyen={QUYEN.tongQuanDoc} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Select<string>
              value={truyVan.projectId}
              onChange={(projectId) => setTruyVan((q) => ({ ...q, projectId }))}
              aria-label={t('rpaTongQuan.locProject')}
              showSearch
              optionFilterProp="label"
              className="w-full sm:w-64"
              options={[
                { value: '', label: t('rpaTongQuan.moiProject') },
                ...(soLieu.data?.data.projects ?? []).map((p) => ({ value: p.id, label: p.name || p.id })),
              ]}
            />
            <Select<number>
              value={truyVan.days}
              onChange={(days) => setTruyVan((q) => ({ ...q, days }))}
              aria-label={t('rpaTongQuan.khoangNgay')}
              className="w-44"
              options={KHOANG_NGAY_TONG_QUAN.map((so) => ({ value: so, label: t('rpaTongQuan.soNgay', { so }) }))}
            />
            {soLieu.data && (
              <span role="status" className="inline-flex items-center gap-1.5 text-xs">
                {soLieu.isError ? (
                  <Badge variant="warning">
                    <WarningOutlined aria-hidden /> {t('rpaTongQuan.khongLamMoiDuoc')}
                  </Badge>
                ) : (
                  <Badge variant="success">{t('rpaTongQuan.thoiGianThuc')}</Badge>
                )}
              </span>
            )}
          </div>

          {soLieu.isPending ? (
            <div className="space-y-4" aria-busy="true">
              <Bento>
                {[0, 1, 2, 3].map((i) => (
                  <Tile key={i} span="sm:col-span-6 lg:col-span-3">
                    <Skeleton className="h-20 w-full" />
                  </Tile>
                ))}
              </Bento>
              <Tile>
                <Skeleton className="h-64 w-full" />
              </Tile>
            </div>
          ) : soLieu.isError && !soLieu.data ? (
            <Tile>
              <LoiRpa loi={soLieu.error} onThuLai={() => void soLieu.refetch()} />
            </Tile>
          ) : (
            <SoLieu du={soLieu.data} />
          )}

          <BangLuot q={luot} />
        </>
      )}
    </div>
  );
}

function SoLieu({ du }: { du: TongQuanRpa }) {
  const { t, locale } = useT();
  const s = du.data.summary;
  const ngay = du.data.byDate;
  const khoang = ngay.length ? `${ngayVN(ngay[0]!.date)} → ${ngayVN(ngay[ngay.length - 1]!.date)}` : '';
  return (
    <>
      {du.meta.truncated && (
        <p role="status" className="text-xs text-warning-700">
          {t('rpaTongQuan.biCat', { so: soVN(du.meta.limit, locale) })}
        </p>
      )}
      <Bento>
        <StatTile nhan={t('rpaTongQuan.kpiProject')} so={soVN(s.totalProjects, locale)} icon={<FolderOutlined />} tone="primary" />
        <StatTile nhan={t('rpaTongQuan.kpiRobot')} so={soVN(s.runningRobots, locale)} icon={<RobotOutlined />} tone="sky" />
        <StatTile
          nhan={t('rpaTongQuan.kpiThanhCong')}
          so={soVN(s.totalSuccess, locale)}
          sub={t('rpaTongQuan.kpiTyLe', { tyLe: soVN(phanTram(s.successRate), locale) })}
          icon={<CheckCircleOutlined />}
          tone="success"
        />
        <StatTile
          nhan={t('rpaTongQuan.kpiLoi')}
          so={soVN(s.totalFailed, locale)}
          sub={t('rpaTongQuan.kpiTyLeLoi', { tyLe: soVN(phanTram(s.failedRate), locale) })}
          icon={<CloseCircleOutlined />}
          tone="danger"
        />

        <Tile span="col-span-12 lg:col-span-8" className="min-w-0">
          <TileHeader
            icon={<BarChartOutlined />}
            tone={TONE}
            title={t('rpaTongQuan.theoNgay')}
            action={khoang ? <span className="text-xs tabular-nums text-content-muted">{khoang}</span> : undefined}
          />
          <TileBody>
            <CotTheoNgay ngay={ngay} rong={t('rpaTongQuan.chuaCoDuLieu')} />
          </TileBody>
        </Tile>
        <Tile span="col-span-12 lg:col-span-4">
          <TileHeader icon={<PieChartOutlined />} tone={TONE} title={t('rpaTongQuan.tyLeTieuDe')} />
          <TileBody>
            <VanhTyLe thanhCong={s.totalSuccess} loi={s.totalFailed} tyLe={s.successRate} />
          </TileBody>
        </Tile>
        <Tile span="col-span-12 lg:col-span-6" className="min-w-0">
          <TileHeader icon={<AppstoreOutlined />} tone={TONE} title={t('rpaTongQuan.theoProject')} mo={t('rpaTongQuan.theoProjectGoiY')} />
          <TileBody>
            <ThanhTheoProject dong={du.data.byProject} rong={t('rpaTongQuan.chuaCoProject')} />
          </TileBody>
        </Tile>
        <Tile span="col-span-12 lg:col-span-6" className="min-w-0">
          <TileHeader icon={<RobotOutlined />} tone={TONE} title={t('rpaTongQuan.tinhTrangRobot')} />
          <TileBody>
            <DanhSachRobot robots={du.data.robots} />
          </TileBody>
        </Tile>
      </Bento>
    </>
  );
}

function BangLuot({ q }: { q: ReturnType<typeof useInstancesTongQuan> }) {
  const { t, locale } = useT();
  const [loc, setLoc] = useState<BoLocTongQuan>({ tim: '', status: '' });
  const [dangXem, setDangXem] = useState<InstanceTongQuan | null>(null);
  const ds = q.data?.data ?? [];
  const loc1 = locInstance(ds, loc);
  const dangLoc = loc.tim.trim() !== '' || loc.status !== '';

  const thoiLuong = (i: InstanceTongQuan) => {
    if (dangChay(i.status)) return <span className="text-xs italic text-content-muted">{t('rpaTongQuan.dangChayNhan')}</span>;
    const ms = thoiLuongMs(i);
    if (ms === null) return <span className="text-content-muted">{KHONG_CO}</span>;
    const { gio, phut, giay } = tachThoiLuong(ms);
    return (
      <span className="whitespace-nowrap text-xs tabular-nums">
        {gio > 0
          ? t('rpaTongQuan.thoiLuongGio', { gio, phut })
          : phut > 0
            ? t('rpaTongQuan.thoiLuongPhut', { phut, giay })
            : t('rpaTongQuan.thoiLuongGiay', { giay })}
      </span>
    );
  };

  return (
    <Tile className="min-w-0">
      <TileHeader
        icon={<UnorderedListOutlined />}
        tone={TONE}
        title={t('rpaTongQuan.danhSachLuot')}
        action={
          ds.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                allowClear
                prefix={<SearchOutlined aria-hidden />}
                placeholder={t('rpaTongQuan.timLuot')}
                aria-label={t('rpaTongQuan.timLuot')}
                value={loc.tim}
                onChange={(e) => setLoc((c) => ({ ...c, tim: e.target.value }))}
                className="w-full sm:w-64"
              />
              <Select<string>
                value={loc.status}
                onChange={(status) => setLoc((c) => ({ ...c, status }))}
                aria-label={t('rpaTongQuan.locTrangThai')}
                className="w-44"
                options={[
                  { value: '', label: t('rpaTongQuan.moiTrangThai') },
                  ...TRANG_THAI_LOC_TONG_QUAN.map((s) => ({ value: s, label: t(TRANG_THAI_INSTANCE[s]!.khoa) })),
                ]}
              />
            </div>
          ) : undefined
        }
      />
      {q.isPending ? (
        <Skeleton className="m-4 h-48" />
      ) : q.isError && !q.data ? (
        <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
      ) : ds.length === 0 ? (
        <Empty icon={HistoryOutlined} title={t('rpaTongQuan.chuaCoLuot')} description={t('rpaTongQuan.chuaCoLuotMoTa')} />
      ) : loc1.length === 0 ? (
        <Empty icon={SearchOutlined} title={t('rpaTongQuan.rongLoc')} />
      ) : (
        <DataTable<InstanceTongQuan>
          caption={t('rpaTongQuan.danhSachLuot')}
          dataSource={loc1}
          rowKey="id"
          size="small"
          pagination={
            loc1.length > TRANG_LUOT_TONG_QUAN
              ? {
                  pageSize: TRANG_LUOT_TONG_QUAN,
                  showSizeChanger: false,
                  showTotal: (tong, [tu, den]) =>
                    t('rpaTongQuan.tongTrang', { tu: soVN(tu, locale), den: soVN(den, locale), tong: soVN(tong, locale) }),
                }
              : false
          }
          columns={[
            { title: t('chung.stt'), key: 'stt', width: 56, render: (_, _i, n) => <span className="tabular-nums">{soVN(n + 1, locale)}</span> },
            {
              title: t('rpaTongQuan.cotThoiGian'),
              key: 'createdAt',
              width: 150,
              render: (_, i) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayGioVN(i.createdAt, locale)}</span>,
            },
            {
              title: t('rpaTongQuan.cotProject'),
              key: 'project',
              render: (_, i) => (
                <span className="block max-w-[12rem] truncate" title={i.projectName || i.projectId}>
                  {i.projectName || i.projectId || <span className="text-content-muted">{t('rpaTongQuan.chuaGanProject')}</span>}
                </span>
              ),
            },
            {
              title: t('rpaTongQuan.cotWorkflow'),
              key: 'workflow',
              render: (_, i) => (
                <div className="min-w-0 max-w-sm leading-tight">
                  <p className="truncate text-content-primary" title={i.name || i.workflowId}>
                    {i.name || i.workflowId || KHONG_CO}
                  </p>
                  {i.errorMessage && (
                    <p className="line-clamp-2 break-words text-xs text-danger-700" title={i.errorMessage}>
                      {i.errorMessage}
                    </p>
                  )}
                </div>
              ),
            },
            { title: t('rpaTongQuan.cotThoiLuong'), key: 'thoiLuong', render: (_, i) => thoiLuong(i) },
            { title: t('rpaTongQuan.cotTrangThai'), key: 'status', render: (_, i) => <BadgeTrangThaiLuot status={i.status} /> },
            {
              title: <span className="sr-only">{t('chung.thaoTac')}</span>,
              key: 'thaoTac',
              width: 64,
              align: 'center',
              fixed: 'right',
              // Một hành động duy nhất ở mọi dòng: nút icon trần, không menu ⋮ (ghi chú của `MenuThaoTac`).
              render: (_, i) => (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<EyeOutlined />}
                  aria-label={t('rpaTongQuan.xemChiTietCua', { ten: i.name || i.workflowId || i.id })}
                  title={t('rpaTongQuan.xemChiTiet')}
                  onClick={() => setDangXem(i)}
                />
              ),
            },
          ]}
        />
      )}
      {ds.length > 0 && (
        <p className="border-t border-default px-4 py-2 text-xs text-content-muted">
          {dangLoc ? t('rpaTongQuan.soLuotLoc', { so: soVN(loc1.length, locale), tong: soVN(ds.length, locale) }) : t('rpaTongQuan.chuThichLuot')}
        </p>
      )}
      {dangXem && <HopBaoCaoInstance dong={dangXem} onClose={() => setDangXem(null)} />}
    </Tile>
  );
}
